import { AfterContentInit, Component, DoCheck, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ActivityInterface } from 'src/app/models/activity.interface';
import { DropDownLink } from 'src/app/models/drop-down-link.interface';
import { Subscription, forkJoin } from 'rxjs';
import { PictureInterface } from 'src/app/models/picture.interface';
import { ActivityComponent } from '../activity/activity.component';
import { LocationContextService } from 'src/app/services/location-context.service';
import { ServiceService } from 'src/app/services/service.service';

@Component({
    selector: 'app-activity-filter',
    templateUrl: './activity-filter.component.html',
    styleUrls: ['./activity-filter.component.scss'],
    standalone: true,
    imports: [RouterModule, ActivityComponent, CommonModule, FormsModule]
})
export class ActivityFilterComponent implements OnInit, OnChanges, OnDestroy, DoCheck , AfterContentInit {
  @Input() searchTitle: string = '';
  languages: DropDownLink[] = [];
  durations: DropDownLink[] = [];
  times: DropDownLink[] = [];
  activities: ActivityInterface[] = [];
  filteredActivities: ActivityInterface[] = [];
  activitiesSorted: ActivityInterface[] = [];
  medianPrice!: number;
  picUrls: PictureInterface[] = [];
  searchText: string = '';
  private subscriptions = new Subscription();
  // Map to store availabilities for each activity (activityId -> Availability[])
  private activityAvailabilities: Map<string, any[]> = new Map();

  constructor(
    public router: Router,
    private locationContext: LocationContextService,
    private service: ServiceService
  ) {}




  compareValues(sortValue) {
    return function innerSort(a) {
      const mainActivityTypeA = a.mainActivityType.toUpperCase(); // ignore upper and lowercase

      if (mainActivityTypeA < sortValue) {
        return -1;
      }
      if (mainActivityTypeA > sortValue) {
        return 1;
      }

      // names must be equal
      return 0;
    };
  }

  ngOnInit() {
    this.loadActivities();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  loadActivities() {
    const location = this.locationContext.location;
    const sub = this.service.getActivitiesByLocation(location).subscribe({
      next: (activities) => {
        if (!activities || activities.length === 0) {
          this.activities = [];
          this.filteredActivities = [];
          return;
        }

        // Load pictures and availabilities for each activity
        const pictureObservables = activities.map(activity => 
          this.service.getPicturesByActivityId(activity.id)
        );
        const availabilityObservables = activities.map(activity => 
          this.service.getAvailabilityByActivityId(activity.id)
        );

        if (pictureObservables.length === 0) {
          this.activities = [];
          this.filteredActivities = [];
          return;
        }

        // Load both pictures and availabilities in parallel
        forkJoin([
          forkJoin(pictureObservables),
          forkJoin(availabilityObservables)
        ]).subscribe({
          next: ([picturesArrays, availabilityArrays]) => {
            // Store availabilities in the map
            activities.forEach((activity, index) => {
              this.activityAvailabilities.set(activity.id, availabilityArrays[index] || []);
            });

            // Map activities to ActivityInterface format
            this.activities = activities.map((activity, index) => {
              const activityPictures = picturesArrays[index] || [];
              
              // Debug logging for picture loading
              if (activityPictures.length === 0) {
                console.warn(`[PICTURE WARNING] Activity "${activity.name}" has no pictures`);
              } else {
                activityPictures.forEach((pic, picIndex) => {
                  if (!pic.url || pic.url.trim() === '') {
                    console.warn(`[PICTURE WARNING] Activity "${activity.name}" picture ${picIndex + 1} has empty or missing URL:`, pic);
                  }
                });
              }
              
              return this.mapActivityToInterface(activity, activityPictures);
            });

            this.applyFilters();
          },
          error: (error) => {
            console.error('Error loading pictures or availabilities:', error);
            // If we can't load availabilities, we can't verify active dates
            // Set empty availabilities for all activities so they won't be shown
            activities.forEach((activity) => {
              this.activityAvailabilities.set(activity.id, []);
            });
            // Still map activities (they'll be filtered out if no active dates)
            this.activities = activities.map(activity => 
              this.mapActivityToInterface(activity, [])
            );
            this.applyFilters();
          }
        });
      },
      error: (error) => {
        console.error('Error loading activities:', error);
        this.activities = [];
        this.filteredActivities = [];
      }
    });

    this.subscriptions.add(sub);
  }

  mapActivityToInterface(activity: any, pictures: any[]): ActivityInterface {
    // FORCE discountedPrice to 0 - no discounts should display unless explicitly enabled
    // This ensures activities without discounts don't show discount UI
    let discountedPrice = 0;
    
    // Uncomment below if you want to allow discounts > 1%
    // if (activity.discountedPrice != null && activity.discountedPrice !== undefined && activity.discountedPrice !== '') {
    //   const priceValue = typeof activity.discountedPrice === 'string' 
    //     ? parseFloat(activity.discountedPrice) 
    //     : Number(activity.discountedPrice);
    //   if (!isNaN(priceValue) && isFinite(priceValue) && priceValue > 1) {
    //     discountedPrice = priceValue;
    //   }
    // }
    
    // Debug log to see what's in the database
    if (activity.discountedPrice != null && activity.discountedPrice !== 0 && activity.discountedPrice !== '0') {
      console.warn(`[DISCOUNT WARNING] Activity "${activity.name}" has discountedPrice=${activity.discountedPrice} in DB but it's being forced to 0`);
    }

    return {
      id: activity.id,
      productId: activity.productId || '',
      name: activity.name || '',
      topPick: activity.topPick || false,
      favorite: activity.favorite || false,
      picUrl: pictures
        .filter(pic => pic && pic.url) // Filter out any null/undefined pictures or empty URLs
        .map(pic => ({
          id: pic.id,
          url: pic.url?.trim() || '', // Ensure URL is trimmed and not empty
          description: pic.description || ''
        })),
      mainActivityType: activity.mainActivityType || '',
      description: activity.description || '',
      fullDescription: activity.fullDescription || '',
      duration: activity.duration || '',
      amenities: [],
      amenityTags: activity.amenityTags ? (typeof activity.amenityTags === 'string' ? activity.amenityTags.split(',').map(t => t.trim()) : activity.amenityTags) : [],
      bookingTag: activity.bookingTag || 0,
      bookingTagActive: activity.bookingTagActive || false,
      sellOut: activity.sellOut || false,
      searchTags: activity.searchTags ? (typeof activity.searchTags === 'string' ? activity.searchTags.split(',').map(t => t.trim()) : activity.searchTags) : [],
      rating: activity.rating || 0,
      reviews: activity.reviews || 0,
      pricePerPerson: activity.pricePerPerson ?? 0,
      discountedPrice: discountedPrice,
      peoplePerPrice: activity.peoplePerPrice || 0,
      currencyType: activity.currencyType || '',
      highlights: activity.highlights ? (typeof activity.highlights === 'string' ? activity.highlights.split(',').map(h => h.trim()) : activity.highlights) : [],
      includes: [],
      notSuitable: activity.notSuitable ? (typeof activity.notSuitable === 'string' ? activity.notSuitable.split(',').map(n => n.trim()) : activity.notSuitable) : [],
      meetingPoint: {} as any,
      impotantInformation: [],
      startDate: activity.startDate || '',
      endDate: activity.endDate || '',
      archived: activity.archived || false
    };
  }

  /**
   * Check if an activity has active (current or future) availability dates
   */
  hasActiveDates(activityId: string): boolean {
    const availabilities = this.activityAvailabilities.get(activityId) || [];
    if (availabilities.length === 0) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

    // Check if there's at least one availability with a current or future date and valid time slots
    return availabilities.some(avail => {
      if (!avail.date) {
        return false;
      }

      try {
        // Parse date string (format: YYYY-MM-DD)
        const dateParts = avail.date.split('-');
        if (dateParts.length !== 3) {
          return false;
        }

        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
        const day = parseInt(dateParts[2], 10);
        const availabilityDate = new Date(year, month, day);
        availabilityDate.setHours(0, 0, 0, 0);

        // Check if date is valid and is today or in the future
        if (isNaN(availabilityDate.getTime())) {
          return false;
        }

        if (availabilityDate < today) {
          return false; // Past date, not active
        }

        // Check if it has valid time slots
        if (!avail.timeSlots) {
          return false;
        }

        try {
          const timeSlots = typeof avail.timeSlots === 'string' 
            ? JSON.parse(avail.timeSlots)
            : avail.timeSlots;
          
          // Only consider it active if it has valid time slots
          return Array.isArray(timeSlots) && timeSlots.length > 0;
        } catch (e) {
          return false; // Invalid time slots JSON
        }
      } catch (error) {
        console.warn(`[AVAILABILITY] Error parsing date for activity ${activityId}:`, error);
        return false;
      }
    });
  }

  applyFilters() {
    let filtered = [...this.activities];

    // Filter out activities without active dates (current or future)
    filtered = filtered.filter(activity => this.hasActiveDates(activity.id));

    // Filter by activity type if searchTitle is provided
    if (this.searchTitle && this.searchTitle.trim() !== '') {
      filtered = filtered.filter(activity => 
        activity.mainActivityType?.toLowerCase().includes(this.searchTitle.toLowerCase()) ||
        activity.name?.toLowerCase().includes(this.searchTitle.toLowerCase())
      );
    }

    // Filter by search text if provided
    if (this.searchText && this.searchText.trim() !== '') {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(activity =>
        activity.name?.toLowerCase().includes(searchLower) ||
        activity.description?.toLowerCase().includes(searchLower) ||
        activity.fullDescription?.toLowerCase().includes(searchLower) ||
        activity.searchTags?.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    this.filteredActivities = filtered;
  }

  onSearchChange() {
    this.applyFilters();
  }
  ngDoCheck(): void {
  }

  ngAfterContentInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchTitle'] && !changes['searchTitle'].firstChange) {
      this.applyFilters();
    }
  }
reloadComponent(self:boolean,urlToNavigateTo ?:string){
  //skipLocationChange:true means dont update the url to / when navigating
 console.log("Current route I am on:",this.router.url);
 const url=self ? this.router.url :urlToNavigateTo;
 this.router.navigateByUrl('/',{skipLocationChange:true}).then(()=>{
   this.router.navigate([`/${url}`]).then(()=>{
     console.log(`After navigation I am on:${this.router.url}`)
   })
 })
}

median(medianPrice: number[]) {
    const sorted = Array.from(medianPrice).sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);

    if (sorted.length % 2 === 0) {
        return (sorted[middle - 1] + sorted[middle]) / 2;
    }

    return sorted[middle];
  }

}
